/* src/utils/timeOffUtils.js
 * ===========================================================================
 * Shared rules for everything stored in the `timeOff` collection.
 *
 * That collection now holds two kinds of record:
 *
 *   1. REQUESTS  (entryType missing or "request")
 *      Planned time off that goes through Pending -> Approved / Denied.
 *      Can be full day(s), or a planned partial day (leave early / come in
 *      late) via `dayType` + `partialTime`.
 *
 *   2. ABSENCES  (entryType === "absence")
 *      Something that already happened and is being logged: someone called
 *      out, left early, or came in late. There is nothing to approve, so the
 *      status is "Recorded". Extra fields:
 *        reasonCategory   Sick, Family emergency, ...
 *        notifiedAt       ISO timestamp of when they told us
 *        noticeGiven      "Same day", "Night before", "3 days ahead", ...
 *        noCallNoShow     true when they never told anyone
 *        loggedBy / loggedByUid / loggedAt
 *
 * Old records have no entryType or dayType and keep behaving exactly as
 * before: a request, full day(s).
 *
 * Calendar.js, EmbeddedCalendar.js, TimeOff.js and TimeOffAdmin.js all read
 * these helpers so a record looks and counts the same on every page.
 * ===========================================================================
 */

export const ENTRY_REQUEST = 'request';
export const ENTRY_ABSENCE = 'absence';

export const ABSENCE_STATUS = 'Recorded';

export const isAbsence = (entry) =>
  String(entry?.entryType || '').trim().toLowerCase() === ENTRY_ABSENCE;

export const isRequest = (entry) => !isAbsence(entry);

/* ------------------------------------------------------------ day types -- */

export const DAY_FULL = 'full';
export const DAY_LEAVE_EARLY = 'leaveEarly';
export const DAY_ARRIVE_LATE = 'arriveLate';

export const getDayType = (entry) => {
  const t = entry?.dayType;
  if (t === DAY_LEAVE_EARLY || t === DAY_ARRIVE_LATE) return t;
  return DAY_FULL;
};

export const isPartialDay = (entry) => getDayType(entry) !== DAY_FULL;

export const ABSENCE_REASONS = [
  'Sick',
  'Family emergency',
  'Personal',
  'Medical appointment',
  'Car trouble',
  'Weather',
  'Other',
];

export const PAY_TYPES_FOR_ABSENCE = ['Unpaid', 'Sick', 'Personal', 'Vacation'];

/* ---------------------------------------------------------------- dates -- */

// "YYYY-MM-DD" -> local Date. new Date("YYYY-MM-DD") is UTC midnight, which
// shows as the previous day west of Greenwich.
export const parseYmd = (ymd) => {
  if (!ymd || typeof ymd !== 'string') return null;
  const [y, m, d] = ymd.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

export const toYmd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const todayYmd = () => toYmd(new Date());

export const formatYmd = (ymd) => {
  const date = parseYmd(ymd);
  if (!date) return ymd || '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const formatRange = (start, end) => {
  if (!start) return '';
  if (!end || end === start) return formatYmd(start);
  return `${formatYmd(start)} - ${formatYmd(end)}`;
};

// "14:30" -> "2:30 PM"
export const formatTime12 = (hhmm) => {
  if (!hhmm || !/^\d{1,2}:\d{2}/.test(hhmm)) return hhmm || '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
};

// Value for <input type="datetime-local"> in the viewer's own timezone.
export const nowDateTimeLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

export const isoToDateTimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

/* --------------------------------------------------------------- notice -- */

/*
 * How much warning did we get? Compared by CALENDAR DAY in local time:
 *
 *   told us on a later day than the absence   -> "After the fact"
 *   told us the same day                      -> "Same day"
 *   told us the day before, 5 PM or later     -> "Night before"
 *   told us the day before, before 5 PM       -> "Day before"
 *   2+ days earlier                           -> "N days ahead"
 *   noCallNoShow                              -> "No call / no show"
 */
export const NOTICE_NO_CALL = 'No call / no show';
export const NIGHT_BEFORE_HOUR = 17;

export const computeNotice = ({ notifiedAt, startDate, noCallNoShow }) => {
  if (noCallNoShow) return NOTICE_NO_CALL;
  const told = notifiedAt ? new Date(notifiedAt) : null;
  const day = parseYmd(startDate);
  if (!told || Number.isNaN(told.getTime()) || !day) return '';

  const toldDay = new Date(told.getFullYear(), told.getMonth(), told.getDate());
  const diff = Math.round((day - toldDay) / 86400000);

  if (diff < 0) return 'After the fact';
  if (diff === 0) return 'Same day';
  if (diff === 1) return told.getHours() >= NIGHT_BEFORE_HOUR ? 'Night before' : 'Day before';
  return `${diff} days ahead`;
};

// Buckets used for counting, so "3 days ahead" and "5 days ahead" group.
export const noticeBucket = (entry) => {
  const n = String(entry?.noticeGiven || '');
  if (entry?.noCallNoShow || n === NOTICE_NO_CALL) return 'noCall';
  if (n === 'Same day' || n === 'After the fact') return 'sameDay';
  if (n === 'Night before' || n === 'Day before') return 'nightBefore';
  if (/days ahead$/.test(n)) return 'ahead';
  return 'unknown';
};

/* ---------------------------------------------------------- descriptions -- */

// Short plain-language description of what happened / is planned.
export const describeEntry = (entry) => {
  const type = getDayType(entry);
  const time = formatTime12(entry?.partialTime);

  if (isAbsence(entry)) {
    if (type === DAY_LEAVE_EARLY) return time ? `Left early at ${time}` : 'Left early';
    if (type === DAY_ARRIVE_LATE) return time ? `Came in late at ${time}` : 'Came in late';
    return entry?.noCallNoShow ? 'No call / no show' : 'Called out';
  }

  if (type === DAY_LEAVE_EARLY) return time ? `Leaving early at ${time}` : 'Leaving early';
  if (type === DAY_ARRIVE_LATE) return time ? `Coming in late at ${time}` : 'Coming in late';
  return 'Time off';
};

/* ------------------------------------------------------------- calendar -- */

// Denied / pending requests stay off the calendar, as before. Logged
// absences always show - there is no approval step for something that
// already happened.
export const shouldShowOnCalendar = (entry) => {
  if (!entry || !entry.startDate) return false;
  if (isAbsence(entry)) return true;
  return String(entry.status || '').trim().toLowerCase() === 'approved';
};

// { "YYYY-MM-DD": [entry, ...] }. Partial days only ever cover one day.
export const buildTimeOffByDate = (entries = []) => {
  const map = {};
  entries.forEach((entry) => {
    if (!shouldShowOnCalendar(entry)) return;
    const start = parseYmd(entry.startDate);
    if (!start) return;
    const endYmd = isPartialDay(entry) ? entry.startDate : entry.endDate || entry.startDate;
    let end = parseYmd(endYmd) || start;
    if (end < start) end = start;

    // Guard against a typo'd year turning into a 10,000-day loop.
    const cursor = new Date(start);
    let guard = 0;
    while (cursor <= end && guard < 400) {
      const key = toYmd(cursor);
      if (!map[key]) map[key] = [];
      map[key].push(entry);
      cursor.setDate(cursor.getDate() + 1);
      guard += 1;
    }
  });
  return map;
};

// One color per KIND so the legend means something. Previously the color
// was derived from the first letter of the person's name.
export const TIME_OFF_COLORS = {
  approvedOff: '#0f766e',   // teal  - approved time off
  plannedPartial: '#0e7490', // cyan  - approved leave early / come in late
  calledOut: '#b91c1c',     // red   - called out
  unplannedPartial: '#c2410c', // orange - left early / came in late
  noCall: '#111827',        // black - no call / no show
};

export const getTimeOffKind = (entry) => {
  if (isAbsence(entry)) {
    if (entry?.noCallNoShow) return 'noCall';
    return isPartialDay(entry) ? 'unplannedPartial' : 'calledOut';
  }
  return isPartialDay(entry) ? 'plannedPartial' : 'approvedOff';
};

export const TIME_OFF_LEGEND = [
  { kind: 'approvedOff', label: 'Approved time off' },
  { kind: 'plannedPartial', label: 'Approved leave early / late' },
  { kind: 'calledOut', label: 'Called out' },
  { kind: 'unplannedPartial', label: 'Left early / came in late' },
  { kind: 'noCall', label: 'No call / no show' },
];

export const getTimeOffChipStyle = (entry) => ({
  backgroundColor: TIME_OFF_COLORS[getTimeOffKind(entry)],
  color: '#fff',
  padding: '4px 6px',
  borderRadius: 5,
  borderLeft: '4px solid rgba(255,255,255,0.85)',
  marginBottom: 4,
  fontSize: 11,
  lineHeight: 1.25,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'normal',
  textAlign: 'left',
});

// Calendar text. The reason is deliberately NOT included: the calendar is
// seen by the whole team, and "why someone was out" stays on the Time Off
// pages.
export const getCalendarLabel = (entry) =>
  `${entry?.name || 'Someone'} · ${describeEntry(entry)}`;
